import React, { useState, useEffect, useContext } from 'react';
import DatahubAPI from '../../api/DatahubAPI';
import CircularProgress from '@mui/material/CircularProgress';
import TradeForm from '../../components/TradeProtection/TradeForm';
import HSCodesHitBreakdown from '../../components/TradeProtection/HSCodesHitBreakdown';
import EntityResolution from 'components/Common/EntityResolution';
import { Typography } from '@mui/material';
import { Redirect } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import SubscribeModal from '../../components/SubscribeModal';
import { useSnackbar } from 'notistack';
import { Player } from '@lottiefiles/react-lottie-player';
import { exportToCSV } from 'components/Common/CSVManage';
import {
  StyledInput,
  NextButton,
} from '../../components/Common/MUIStyledComponents';
import Modal from 'react-modal';
import { Close } from '@mui/icons-material';
import ModuleHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';
import { ModuleContentTooltip } from '../../components/Common/MUIStyledComponents';

const TradeProtectionReact: React.FC = () => {
  // PAYMENTS STATE AND FUNCTIONS
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const [ userHasTrade, setUserHasTrade ] = useState(false);
  const [ activeSub, setActiveSub ] = useState(false);
  const [ matchedTeamWithStripe, setMatchedTeamWithStripe ] =
    useState<string>('');

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const checkForTradeAndTrial = () => {
    if (
      cur_user.selected_team?.attributes?.tier?.includes('enterprise') ||
      cur_user.selected_team?.attributes?.tier?.includes('standard') ||
      cur_user.selected_team?.attributes?.tier?.includes('pro')
    ) {
      setUserHasTrade(true);
    }
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setMatchedTeamWithStripe(cus.id);
          cus.subscriptions.data.map((sub: any) => {
            if (
              sub.metadata.moduleName === 'BITE Standard' ||
              sub.metadata.moduleName === 'BITE Pro' ||
              sub.metadata.moduleName === 'BITE Enterprise'
            ) {
              setActiveSub(true);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    checkForTradeAndTrial();
  }, [ activeSub ]);

  // PAGE STATE AND FUNCTIONS
  const [ tableData, setTableData ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ aggData, setAggData ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });
  const [ tradePrimaryOpts, setTradePrimaryOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ codeOpts, setCodeOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ chapterOpts, setChapterOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ listOpts, setListOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ sourceCountryOpts, setSourceCountryOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ agencyOpts, setAgencyOpts ] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [ pageNum, setPageNum ] = useState<number>(0);
  const [ selectedIndex, setSelectedIndex ] = useState<string>('HS Codes');
  const [ entityToggle, setEntityToggle ] = useState(false);
  const [ fileNameModalOpen, setFileNameModalOpen ] = useState(false);
  const [ fileName, setFileName ] = useState('');

  // file modal
  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameModalOpen(false);
    setFileName('');
  };

  const toggleModal = () => {
    setFileNameModalOpen(true);
  };

  const handleCSV = () => {
    if (tableData.data.hits.hits.length >= 1) {
      switch (selectedIndex) {
      case 'HS Codes':
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          [ 'HS Code', 'Section', 'Chapter', 'Product Name' ],
          'hsCodes',
        );
        break;
      case 'Regimes':
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          [
            'HS Code',
            'Product Name',
            'Agency',
            'Source Country',
            'Risk Country',
          ],
          'regimes',
        );
        break;
      case 'CET':
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          [
            'Title',
            'Applicant',
            'US Patent Application',
            'USPTO Post Date',
            'Inventors',
          ],
          'cet',
        );
        break;
      case 'Patent':
        exportToCSV(
          fileName,
          [ ...tableData.data.hits.hits ],
          [
            'Application Number',
            'Invention Title',
            'Filing Date',
            'Grant Date',
          ],
          'patent',
        );
        break;
      default:
        enqueueSnackbar('Error Exporting Data', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('No Result Data to Export', { variant: 'error' });
    }
  };

  // FORM VALUES
  const [ formValues, setFormValues ] = useState({
    searchValue: '',
    hsCodeSection: [],
    hsCodeChapter: [],
    hsCode: [],
    sourceCountry: [],
    agency: [],
    state: [],
    listName: [],
    fuzziness: 0,
  });

  const handleIndexChange = (index: string) => {
    switch (index) {
    case 'HS Codes':
      setSelectedIndex('HS Codes');
      break;
    case 'Regimes':
      setSelectedIndex('Regimes');
      break;
    case 'CET':
      setSelectedIndex('CET');
      break;
    case 'Patent':
      setSelectedIndex('Patent');
      break;
    default:
      setSelectedIndex('HS Codes');
    }
  };

  const getInitialResults = async() => {
    const res = await DatahubAPI.trade({
      indexName: selectedIndex,
      agg: true,
      pageNum: pageNum,
    });
    if (res?.success) {
      setAggData({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
      setTableData({ isLoading: false, data: res?.result?.result?.body });
      setTradePrimaryOpts({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
    }
  };

  const queryResults = async(values: any, page: number) => {
    let fuzzyValue = '';
    if (entityToggle) {
      fuzzyValue = 'AUTO';
    } else {
      fuzzyValue = '0';
    }
    const res = await DatahubAPI.trade({
      agg: true,
      indexName: selectedIndex,
      pageNum: page,
      primary: values.searchValue,
      listName: values.listName,
      hsCodeSection: values.hsCodeSection,
      hsCodeChapter: values.hsCodeChapter,
      hsCode: values.hsCode,
      sourceCountry: values.sourceCountry,
      agency: values.agency,
      state: values.state,
      fuzziness: fuzzyValue,
    });
    if (res?.success) {
      setAggData({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
      setTableData({ isLoading: false, data: res?.result?.result?.body });
    } else {
      enqueueSnackbar(
        'Error retrieving data. Commodities and Products data may be being updated',
        { variant: 'error', autoHideDuration: 5000 },
      );
    }
  };

  useEffect(() => {
    setFormValues({
      searchValue: '',
      hsCodeSection: [],
      hsCodeChapter: [],
      hsCode: [],
      sourceCountry: [],
      agency: [],
      state: [],
      listName: [],
      fuzziness: 0,
    });
    getInitialResults();
  }, [ selectedIndex ]);

  // dynamic table data filtering on dropdown option select/deselect
  useEffect(() => {
    queryResults(formValues, pageNum);
  }, [
    formValues.hsCodeSection,
    formValues.hsCodeChapter,
    formValues.hsCode,
    formValues.sourceCountry,
    formValues.agency,
    formValues.state,
    formValues.listName,
  ]);

  const helpContent = (
    <p className='module-header-tooltip-text'>
      The Commodities and Products module serves as a database where you can search
      commodities related to various global controls. Use the search bar to look
      up specific queries (HS Codes or commodities). Use Advanced Filters to
      select specific Listing Countries and List Names.
    </p>
  );

  return (
    <ModuleHeaderLayout title='Commodities and Products' helpContent={helpContent}>
      {activeSub && !userHasTrade ? (
        <div className='no-license-modal'>
          <Player
            autoplay
            loop
            src='https://lottie.host/3c23c1ad-7f71-4e3d-8f19-69154715dbdc/SdWWjCmU1J.json'
            style={{ width: '20%' }}
          ></Player>
          <Typography
            variant='h6'
            className='text-white'
            style={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            }}
          >
            You need an assigned license to access this module
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginTop: '20px',
              color: '#BDBEC1',
            }}
          >
            Visit Account &gt; Users &gt; to assign an available license to your
            user or contact your team admin
          </Typography>
        </div>
      ) : !userHasTrade ? (
        <SubscribeModal
          name='BITE High Risk Commodities'
          description='Search for commodities and HS/HTS codes that require additional screening'
          price={75}
          curUser={cur_user}
          stripeTeam={matchedTeamWithStripe}
          activeSub={activeSub}
        />
      ) : (
        <></>
      )}
      <div className={`os-iframe${!userHasTrade ? '-blur' : ''}`}>
        <div className='bl-index-select'>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('HS Codes')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  This dashboard is a simple lookup for HS Codes. Use the search
                  bar to look up specific queries (HS Codes or commodities). Use
                  Advanced Filters to select specific Listing Countries and List
                  Names.
                </p>
              }
            >
              <div className='bl-item-title'>HS Codes / Schedule B</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'HS Codes' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Regimes')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  This dashboard includes control lists related to multiple
                  regimes, controls, and conventions. Use the search bar to look
                  up specific queries (HS Codes or commodities). Use Advanced
                  Filters to select specific Listing Countries and List Names.
                </p>
              }
            >
              <div className='bl-item-title'>
                Regimes / Controls / Conventions
              </div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Regimes' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => handleIndexChange('Patent')}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, #202227)',
                    'maxWidth': '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, #202227)',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  Use the search bar to look up specific queries (HS Codes or
                  commodities). Select from two datasets: the entire patent
                  database or critical and emerging technologies.
                </p>
              }
            >
              <div className='bl-item-title'>Patent Data</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Patent' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
        </div>
        {selectedIndex === 'Patent' && (
          <div className='embargoed-options-container'>
            <div className='select-one-text'>Select One</div>
            <div className='embargoed-country-select-container'>
              <div
                className='country-button-option-selected'
                onClick={() => handleIndexChange('Patent')}
              >
                <div className='country-btn-text'>Patent Examination Data</div>
              </div>
              <div
                className='country-button-option'
                onClick={() => handleIndexChange('CET')}
              >
                <div className='country-btn-text'>
                  Critical & Emerging Technology Patents
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedIndex === 'CET' && (
          <div className='embargoed-options-container'>
            <div className='select-one-text'>Select One</div>
            <div className='embargoed-country-select-container'>
              <div
                className='country-button-option'
                onClick={() => handleIndexChange('Patent')}
              >
                <div className='country-btn-text'>Patent Examination Data</div>
              </div>
              <div
                className='country-button-option-selected'
                onClick={() => handleIndexChange('CET')}
              >
                <div className='country-btn-text'>
                  Critical & Emerging Technology Patents
                </div>
              </div>
            </div>
          </div>
        )}
        <TradeForm
          queryResults={queryResults}
          pageNum={pageNum}
          selectedIndex={selectedIndex}
          formValues={formValues}
          setFormValues={setFormValues}
          tradePrimaryOpts={tradePrimaryOpts}
          setTradePrimaryOpts={setTradePrimaryOpts}
          aggData={aggData}
          codeOpts={codeOpts}
          setCodeOpts={setCodeOpts}
          chapterOpts={chapterOpts}
          setChapterOpts={setChapterOpts}
          listOpts={listOpts}
          setListOpts={setListOpts}
          sourceCountryOpts={sourceCountryOpts}
          setSourceCountryOpts={setSourceCountryOpts}
          agencyOpts={agencyOpts}
          setAgencyOpts={setAgencyOpts}
          getInitialResults={getInitialResults}
        />
        <EntityResolution
          entityToggle={entityToggle}
          setEntityToggle={setEntityToggle}
        />
        {tableData.isLoading ? (
          <div>
            <CircularProgress style={{ color: '#0E8BFF' }} />
          </div>
        ) : (
          <HSCodesHitBreakdown
            toggleModal={toggleModal}
            selectedIndex={selectedIndex}
            queryResults={queryResults}
            formValues={formValues}
            pageNum={pageNum}
            setPageNum={setPageNum}
            tableData={tableData}
          />
        )}
        <Modal
          isOpen={fileNameModalOpen}
          onRequestClose={handleFileNameClose}
          ariaHideApp={false}
        >
          <div className='bite-score-file-modal'>
            <div className='display-flex justify-between width-100'>
              <Typography variant='h5'>File Name:</Typography>
              <Close onClick={handleFileNameClose} className='cursor-pointer' />
            </div>
            <div className='display-flex justify-end width-100'>
              <StyledInput
                value={fileName}
                onChange={handleFileNameChange}
                variant='outlined'
                className='margin-right-20px'
              />
              <NextButton
                variant='contained'
                color='primary'
                onClick={() => {
                  handleCSV();
                  handleFileNameClose();
                }}
              >
                Download
              </NextButton>
            </div>
          </div>
        </Modal>
      </div>
    </ModuleHeaderLayout>
  );
};

export default TradeProtectionReact;
